import { Component, OnInit, OnDestroy, Inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { OverlayContainer } from "@angular/cdk/overlay";
import { DogService } from "src/app/core/services/dog.service";
import { DogSummary } from "src/app/shared/models/dog-summary";
import { Vet } from "src/app/shared/interfaces/vet.interface";
import { CustomerService } from "src/app/core/services/customer.service";
import { BookingService } from "src/app/core/services/booking.service";
import { CustomerSummary } from "src/app/shared/models/customer-summary";
import { NotificationMessageComponent } from "src/app/shared/components/notification/notification-message/notification-message.component";
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MessageUsDialogComponent } from "src/app/modules/main/pages/contact/message-us-dialog/message-us-dialog.component";
import { MatButtonModule } from "@angular/material/button";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn, ReactiveFormsModule } from "@angular/forms";
import { MatFormField, MatLabel, MatError, MatSuffix } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatSelect } from "@angular/material/select";
import { MatOption } from "@angular/material/core";
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from "@angular/material/datepicker";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { Observable, forkJoin, ReplaySubject, Subject } from "rxjs";
import { takeUntil, finalize, catchError, map } from "rxjs/operators";
import { of } from "rxjs";
import { treatmentDateValidator } from "src/app/core/validators/treatment-date-validator";
import { checkIfHtmlExists } from "src/app/core/validators/sanitize-validator";
import { CONTACT_NUMBER_REGEX, differentContactNumberValidator } from "src/app/core/validators/contact-number-validator";
import * as moment from "moment";

interface DogFormData {
  files: File[];
  dateSelected?: Date;
}

interface MissingDogDetails {
  dog_id: number;
  dog_name?: string;
  missing_chip_number?: boolean;
  missing_chip_number_length?: boolean;
  missing_vet?: boolean;
  missing_worming_treatment?: boolean;
  missing_flea_treatment?: boolean;
}

interface MissingDetails {
  alt_contact?: boolean;
  next_service_at?: string;
  missing_alt_contact_name?: boolean;
  missing_alt_contact_number?: boolean;
  dogs?: MissingDogDetails[];
}

@Component({
  selector: "app-upload-vaccination",
  templateUrl: "./upload-vaccination.component.html",
  styleUrls: ["./upload-vaccination.component.scss"],
  standalone: true,
  imports: [
    CommonModule, MatDialogModule, MatButtonModule, MatProgressSpinnerModule, ReactiveFormsModule,
    MatFormField, MatLabel, MatError, MatSuffix, MatInput, MatSelect, MatOption,
    MatDatepicker, MatDatepickerInput, MatDatepickerToggle, NgxMatSelectSearchModule,
  ]
})
export class UploadVaccinationComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private container: OverlayContainer;

  // Loading data
  uploadLoading: boolean = false;
  uploadError: string | null = null;

  // State data
  disableUploadButton: boolean = true;
  dogData: DogSummary[] = [];
  dogFormData: Map<number, DogFormData> = new Map<number, DogFormData>();
  vaxMinDate = new Date();
  startDate = new Date(this.vaxMinDate.getFullYear() + 1, this.vaxMinDate.getMonth(), this.vaxMinDate.getDate());

  // Which dogs actually need a vaccination-record upload (vax due/expired, not just any dog on the account)
  dogsNeedingVaxUpload: Set<number> = new Set<number>();

  // Missing-details data (chip number, worm/flea treatments, alt contact)
  missingDetails: MissingDetails;
  dogDetailsForms: Map<number, UntypedFormGroup> = new Map<number, UntypedFormGroup>();
  altContactForm: UntypedFormGroup | null = null;
  wormingTreatmentFrequencyOptions = [3, 4, 5, 6];
  fleaTreatmentFrequencyOptions = [1, 2, 3, 4, 5, 6];
  // Treatment dates must be more than 3 days out, so the earliest selectable day is day 4.
  treatmentMinDate: Date;
  vets: Vet[] = [];
  vetFilterCtrl = new UntypedFormControl("");
  filteredVets: ReplaySubject<Vet[]> = new ReplaySubject<Vet[]>(1);

  private sleepOverData: any = null;
  private customerData: CustomerSummary = null;

  vaccinationDue: any;
  vaccinationMessage: string;
  displayMessage: string;

  constructor(
    overlayContainer: OverlayContainer,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<UploadVaccinationComponent>,
    private dogService: DogService,
    private customerService: CustomerService,
    private bookingService: BookingService,
    private formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: { missingDetails?: MissingDetails, vaccinationDue?: any, message?: string }
  ) {
    // Creates the blue backdrop for the dialog.
    overlayContainer.getContainerElement().classList.add("broadcast-dialog_overlay");
    this.container = overlayContainer;
    this.treatmentMinDate = new Date();
    this.treatmentMinDate.setDate(this.treatmentMinDate.getDate() + 4);
    this.missingDetails = (this.data && this.data.missingDetails) || {};
    this.vaccinationDue = this.data && this.data.vaccinationDue;
    this.vaccinationMessage = this.data && this.data.message;
  }

  ngOnInit(): void {
    this.fetchDogInfo();
    this.displayMessage = this.buildDisplayMessage();
    this.fetchContext();
  }

  buildDisplayMessage(): string {
    // When both a vaccination reminder and missing profile details apply at once,
    // show both messages together — otherwise the vax message alone leaves the
    // extra fields below (e.g. microchip number) looking unexplained.
    const parts: string[] = [];
    if (this.vaccinationMessage) {
      parts.push(this.vaccinationMessage);
    }

    const missingDetailsMessage = this.buildMissingDetailsMessage();
    if (missingDetailsMessage) {
      parts.push(missingDetailsMessage);
    }

    // Inline-styled rather than the .divider class: this HTML is set via [innerHtml],
    // so Angular's view-encapsulated component styles don't reach it.
    return parts.join('<hr style="border:none;border-top:1px solid #e2e8f0;margin:24px 0;">');
  }

  buildMissingDetailsMessage(): string {
    if (!this.hasMissingDetails()) {
      return "";
    }

    const lines: string[] = [];
    (this.missingDetails.dogs || []).forEach(dog => {
      const missing: string[] = [];
      if (dog.missing_chip_number) { missing.push("microchip number"); }
      if (dog.missing_chip_number_length) { missing.push("Invalid microchip number"); }
      if (dog.missing_vet) { missing.push("vet details"); }
      if (dog.missing_worming_treatment) { missing.push("worming treatment date"); }
      if (dog.missing_flea_treatment) { missing.push("flea treatment date"); }

      const dogName = dog.dog_name || "Your dog";
      if (missing.length > 0) {
        lines.push(`${dogName}: ${missing.join(", ")}`);
      }
    });
    if (this.missingDetails.missing_alt_contact_name && this.missingDetails.missing_alt_contact_number) {
      lines.push("An alternative contact name and phone number");
    } else if (this.missingDetails.missing_alt_contact_name) {
      lines.push("An alternative contact name");
    } else if (this.missingDetails.missing_alt_contact_number) {
      lines.push("Add an alternative contact phone number");
    }

    if (lines.length === 0) {
      return "";
    }

    // Missing compliance details carry the same day-banking risk as an expired
    // vaccination record, so always surface that urgency here — regardless of whether a
    // vaccination message is also shown. We can't assume the vaccination message already
    // mentions banking: an "expiring soon" reminder does not, only an expired one does.
    const lead = this.vaccinationMessage
      ? "<strong>We're also missing some required details.</strong><br>"
      : "<strong>Urgent action needed:</strong> day care service now on hold.<br>";
    // Use the top-level next service date as the customer-wide deadline — it's present even
    // when the dogs array is empty (e.g. only a customer-level detail like the emergency
    // contact is missing), which per-dog dates can't cover.
    const nextServiceDate = this.missingDetails.next_service_at
      ? moment(this.missingDetails.next_service_at).format("D MMMM YYYY")
      : null;
    const bankingSentence = nextServiceDate
      ? `To avoid your next scheduled day on <strong>${nextServiceDate}</strong> being banked, please update the following:`
      : "To avoid your next scheduled day being banked, please update the following:";
    const intro = `<span class="urgent-text">${lead}<br>${bankingSentence}</span>`;
    const list = `<ul>${lines.map(line => `<li>${line}</li>`).join("")}</ul>`;
    return `${intro}${list}`;
  }

  ngOnDestroy(): void {
    // Complete the destroy subject to unsubscribe from all observables
    this.destroy$.next();
    this.destroy$.complete();

    // Removes the blue backdrop for the dialog.
    this.container.getContainerElement().classList.remove("broadcast-dialog_overlay");
  }

  hasMissingDetails(): boolean {
    return !!this.missingDetails?.missing_alt_contact_name
      || !!this.missingDetails?.missing_alt_contact_number
      || !!(this.missingDetails?.dogs && this.missingDetails.dogs.length > 0);
  }

  fetchContext(): void {
    // Always fetch sleepover info — it carries each dog's vax_due/vax_expired
    // status, which decides whether the file-upload section shows for that dog,
    // independent of whether it also has any missing profile details.
    // Each request catches its own failure so one bad request can't blank out
    // the others' results — forkJoin errors out entirely otherwise.
    const requests: { [key: string]: Observable<any> } = {
      sleepOver: this.bookingService.getSleepOverInformation().pipe(
        catchError(error => {
          console.error("Failed to load sleepover information:", error);
          this.uploadError = "Failed to load your vaccination and health details. Please refresh the page.";
          return of(null);
        })
      ),
    };
    if (this.missingDetails.missing_alt_contact_name || this.missingDetails.missing_alt_contact_number) {
      requests.customer = this.customerService.getCustomerSummary().pipe(
        catchError(error => {
          console.error("Failed to load customer summary:", error);
          this.uploadError = "Failed to load your account details. Please refresh the page.";
          return of(null);
        })
      );
    }
    if (this.missingDetails.dogs?.some(dog => dog.missing_vet)) {
      requests.vets = this.dogService.getVets().pipe(
        catchError(error => {
          console.error("Failed to load vets:", error);
          this.uploadError = "Failed to load the list of vets. Please refresh the page.";
          return of(null);
        })
      );
    }

    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any) => {
          if (result.vets) {
            this.vets = result.vets || [];
            this.filteredVets.next(this.vets.slice());
            this.vetFilterCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.filterVets());
          }
          if (result.sleepOver) {
            this.sleepOverData = result.sleepOver["sleepOverInformation"];
            this.computeDogsNeedingVaxUpload();
            if (this.hasMissingDetails()) {
              this.buildDogDetailsForms();
            }
          }
          if (result.customer) {
            this.customerData = result.customer;
            this.buildAltContactForm();
          }
        },
        error: err => {
          console.error("Failed to load dialog context:", err);
        }
      });
  }

  computeDogsNeedingVaxUpload(): void {
    (this.sleepOverData?.["medical_issues_allergies"] || []).forEach((dog: any) => {
      const needsUpload = (dog.vax_due || dog.vax_expired) && !dog.vaccinations_pending_approval;
      if (needsUpload) {
        this.dogsNeedingVaxUpload.add(dog.id);
      }
    });
  }

  shouldShowDogSection(dogId: number): boolean {
    return this.dogsNeedingVaxUpload.has(dogId) || this.dogDetailsForms.has(dogId);
  }

  visibleDogs(): DogSummary[] {
    return this.dogData.filter(dog => this.shouldShowDogSection(dog.id));
  }

  filterVets(): void {
    if (!this.vets) {
      return;
    }
    let search = this.vetFilterCtrl.value;
    if (!search) {
      this.filteredVets.next(this.vets.slice());
      return;
    }
    search = search.toLowerCase();
    this.filteredVets.next(this.vets.filter(vet => vet.name.toLowerCase().includes(search)));
  }

  onVetSelectOpenedChange(opened: boolean): void {
    if (opened) {
      this.vetFilterCtrl.setValue("");
    }
  }

  openVetNotFoundDialog(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: "Can't find your vet?",
      message: "If your vet is not listed, please send the details to us and we'll add it to our database.",
      subject: "Re: Vet details",
      confirmButton: "Submit",
      cancelButton: "Cancel",
    };

    const dialogRef = this.dialog.open(MessageUsDialogComponent, dialogConfig);
    dialogRef.componentInstance.sendMessage.subscribe(result => {
      if (result === true) {
        this.dialog.open(NotificationMessageComponent, {
          data: { heading: "Success!", body: "Your message has been sent and we will follow up with you soon.", type: "success" }
        });
      }
    });
  }

  buildDogDetailsForms(): void {
    (this.missingDetails.dogs || []).forEach(missing => {
      const controls: { [key: string]: UntypedFormControl } = {};
      const validators: ValidatorFn[] = [];

      if (missing.missing_chip_number || missing.missing_chip_number_length) {
        controls.chip_number = new UntypedFormControl("", [Validators.minLength(10)]);
      }
      if (missing.missing_vet) {
        controls.vet_detail_id = new UntypedFormControl(null);
      }
      if (missing.missing_worming_treatment) {
        controls.worming_treatment_date = new UntypedFormControl("");
        controls.worming_treatment_frequency = new UntypedFormControl(this.wormingTreatmentFrequencyOptions[0]);
        validators.push(treatmentDateValidator("worming_treatment_date", "worming_treatment_frequency", "worming_treatment_date"));
      }
      if (missing.missing_flea_treatment) {
        controls.flea_treatment_date = new UntypedFormControl("");
        controls.flea_treatment_frequency = new UntypedFormControl(this.fleaTreatmentFrequencyOptions[0]);
        validators.push(treatmentDateValidator("flea_treatment_date", "flea_treatment_frequency", "flea_treatment_date"));
      }

      if (Object.keys(controls).length === 0) {
        return;
      }

      const group = this.formBuilder.group(controls, { validators });
      group.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.checkUploadButtonState());
      this.dogDetailsForms.set(missing.dog_id, group);
    });
  }

  buildAltContactForm(): void {
    const primaryPhone = this.customerData?.phone || "";
    const controls: { [key: string]: UntypedFormControl } = {};

    if (this.missingDetails.missing_alt_contact_name) {
      controls.alt_contact_firstname = new UntypedFormControl("", [Validators.required, Validators.maxLength(100), checkIfHtmlExists]);
    }
    if (this.missingDetails.missing_alt_contact_number) {
      controls.alt_contact_number = new UntypedFormControl("", [
        Validators.pattern(CONTACT_NUMBER_REGEX),
        checkIfHtmlExists,
        Validators.maxLength(20),
        differentContactNumberValidator(primaryPhone),
      ]);
    }

    if (Object.keys(controls).length === 0) {
      return;
    }

    this.altContactForm = this.formBuilder.group(controls);
    this.altContactForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.checkUploadButtonState());
  }

  dogFormHasContent(form: UntypedFormGroup): boolean {
    const value = form.value;
    return !!(value.chip_number || value.vet_detail_id || value.worming_treatment_date || value.flea_treatment_date);
  }

  altContactFormHasContent(form: UntypedFormGroup): boolean {
    const value = form.value;
    return !!(value.alt_contact_firstname || value.alt_contact_number);
  }

  hasInvalidDetailsForms(): boolean {
    // Only a form the customer actually started filling in can block submission —
    // an untouched (blank) form shouldn't stop them submitting just the vax files.
    let invalid = false;
    this.dogDetailsForms.forEach(form => {
      if (this.dogFormHasContent(form) && form.invalid) {
        invalid = true;
      }
    });
    if (this.altContactForm && this.altContactFormHasContent(this.altContactForm) && this.altContactForm.invalid) {
      invalid = true;
    }
    return invalid;
  }

  markDetailsFormsTouched(): void {
    this.dogDetailsForms.forEach(form => {
      if (this.dogFormHasContent(form)) {
        form.markAllAsTouched();
      }
    });
    if (this.altContactForm && this.altContactFormHasContent(this.altContactForm)) {
      this.altContactForm.markAllAsTouched();
    }
  }

  onFileSelected(event: Event, dogId: number): void {
    const target = event.target as HTMLInputElement;
    if (!target.files) {
      return;
    }

    let currentData: DogFormData | undefined = this.dogFormData.get(dogId);
    if (!currentData) {
      currentData = { files: [] };
    }

    for (let i = 0; i < target.files.length; i++) {
      const file = target.files[i];
      if (file) {
        currentData.files.push(file);
      }
    }

    this.dogFormData.set(dogId, currentData);
    this.checkUploadButtonState();
  }

  onRemoveFile(event: Event, file: File, dogId: number): void {
    event.preventDefault();

    const currentData: DogFormData | undefined = this.dogFormData.get(dogId);
    if (!currentData) {
      return;
    }

    currentData.files = currentData.files.filter((f: File) => f.name !== file.name);
    this.dogFormData.set(dogId, currentData);
    this.checkUploadButtonState();
  }

  hasFiles(dogId: number): boolean {
    const data = this.dogFormData.get(dogId);
    return data ? data.files.length > 0 : false;
  }

  checkUploadButtonState(): void {
    let hasContent = false;

    this.dogFormData.forEach((formData: DogFormData) => {
      if (formData.files.length > 0) {
        hasContent = true;
      }
    });

    this.dogDetailsForms.forEach(form => {
      if (this.dogFormHasContent(form)) {
        hasContent = true;
      }
    });

    if (this.altContactForm && this.altContactFormHasContent(this.altContactForm)) {
      hasContent = true;
    }

    this.disableUploadButton = !hasContent;
  }

  buildSleepOverPayload(): any | null {
    if (!this.sleepOverData || this.dogDetailsForms.size === 0) {
      return null;
    }

    let hasChanges = false;
    const medicalIssuesAllergies = (this.sleepOverData["medical_issues_allergies"] || []).map(dog => {
      const flat = {
        id: dog.id,
        name: dog.name,
        medical_issues_allergies: dog.medical_issues_allergies,
        vet_detail_id: dog.vet_details ? dog.vet_details.id : null,
        chip_number: dog.chip_number,
        worming_treatment_date: dog.worming_treatment_date,
        worming_treatment_frequency: dog.worming_treatment_frequency,
        flea_treatment_date: dog.flea_treatment_date,
        flea_treatment_frequency: dog.flea_treatment_frequency,
      };

      const detailsForm = this.dogDetailsForms.get(dog.id);
      if (!detailsForm || !this.dogFormHasContent(detailsForm)) {
        return flat;
      }

      const value = detailsForm.value;
      if (detailsForm.contains("chip_number") && value.chip_number) {
        flat.chip_number = value.chip_number;
        hasChanges = true;
      }
      if (detailsForm.contains("vet_detail_id") && value.vet_detail_id) {
        flat.vet_detail_id = value.vet_detail_id;
        hasChanges = true;
      }
      if (detailsForm.contains("worming_treatment_date") && value.worming_treatment_date) {
        flat.worming_treatment_date = moment(value.worming_treatment_date).format("YYYY-MM-DD");
        flat.worming_treatment_frequency = value.worming_treatment_frequency;
        hasChanges = true;
      }
      if (detailsForm.contains("flea_treatment_date") && value.flea_treatment_date) {
        flat.flea_treatment_date = moment(value.flea_treatment_date).format("YYYY-MM-DD");
        flat.flea_treatment_frequency = value.flea_treatment_frequency;
        hasChanges = true;
      }

      return flat;
    });

    if (!hasChanges) {
      return null;
    }

    return {
      sleepover: {
        lead_permission: this.sleepOverData["lead_permission"],
        dog_recall: this.sleepOverData["dog_recall"],
        sleeping_routine: this.sleepOverData["sleeping_routine"],
        feeding_details: this.sleepOverData["feeding_details"],
        ice_contact: this.sleepOverData["ice_contact"],
        medical_issues_allergies: medicalIssuesAllergies,
      }
    };
  }

  buildCustomerPayload(): any | null {
    if (!this.altContactForm || !this.altContactFormHasContent(this.altContactForm)) {
      return null;
    }

    // update_customer's params are all optional and only touch the keys present in the
    // request — no need to resend cell/first_name/surname/email etc. Sending cell as an
    // empty string actively overwrites the customer's real phone number server-side.
    // Only include whichever alt-contact field the form actually collected.
    const value = this.altContactForm.value;
    const payload: any = {};
    if (this.altContactForm.contains("alt_contact_firstname") && value.alt_contact_firstname?.trim()) {
      payload.alt_contact_firstname = value.alt_contact_firstname;
    }
    if (this.altContactForm.contains("alt_contact_number") && value.alt_contact_number?.trim()) {
      payload.alt_contact_number = value.alt_contact_number;
    }
    return payload;
  }

  onSubmit(event: Event): void {
    event.preventDefault();

    if (this.uploadLoading || this.disableUploadButton) {
      return;
    }

    if (this.hasInvalidDetailsForms()) {
      this.markDetailsFormsTouched();
      return;
    }

    this.uploadLoading = true;
    this.uploadError = null;

    const subscriptions: Observable<any>[] = [];

    this.dogFormData.forEach((data: DogFormData, dogId: number) => {
      if (data.files.length === 0) {
        return; // Skip dogs with no files
      }

      const vaxRecordsFormData = new FormData();
      vaxRecordsFormData.append("dog_id", dogId.toString());

      if (data.dateSelected) {
        vaxRecordsFormData.append("expiry_date", data.dateSelected.toString());
      }

      for (const file of data.files) {
        vaxRecordsFormData.append("files[]", file, file.name);
      }

      const dogName = this.dogData.find(d => d.id === dogId)?.name || "your dog";
      subscriptions.push(
        this.customerService.uploadVaccinationRecords(vaxRecordsFormData).pipe(
          map(() => ({ success: true, label: `Vaccination records for ${dogName}` })),
          catchError(error => {
            console.error("Upload error for dog:", dogId, error);
            return of({ success: false, label: `Vaccination records for ${dogName}`, message: this.extractErrorMessage(error) });
          })
        )
      );
    });

    const sleepOverPayload = this.buildSleepOverPayload();
    if (sleepOverPayload) {
      subscriptions.push(
        this.bookingService.updateSleepOverInformation(sleepOverPayload).pipe(
          map(() => ({ success: true, label: "Health details" })),
          catchError(error => {
            console.error("Health details update error:", error);
            return of({ success: false, label: "Health details", message: this.extractErrorMessage(error) });
          })
        )
      );
    }

    const customerPayload = this.buildCustomerPayload();
    if (customerPayload) {
      subscriptions.push(
        this.customerService.updateCustomer(customerPayload).pipe(
          map(() => ({ success: true, label: "Alternative contact details" })),
          catchError(error => {
            console.error("Alt contact update error:", error);
            return of({ success: false, label: "Alternative contact details", message: this.extractErrorMessage(error) });
          })
        )
      );
    }

    if (subscriptions.length === 0) {
      this.uploadLoading = false;
      return;
    }

    // Wait for all subscriptions to finish before continuing
    forkJoin(subscriptions)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.uploadLoading = false;
        })
      )
      .subscribe({
        next: results => {
          const failures = results.filter((result: any) => result && result.success === false);
          if (failures.length > 0) {
            this.uploadError = failures.map((f: any) => `${f.label}: ${f.message}`).join("\n");
          } else {
            this.showSuccessDialog();
          }
        },
        error: error => {
          console.error("Upload error:", error);
          this.uploadError = "Failed to upload vaccination records. Please try again.";
        }
      });
  }

  extractErrorMessage(error: any): string {
    return (error && error.error && error.error.message) || "Something went wrong. Please try again.";
  }

  showSuccessDialog(): void {
    this.dialogRef.close();

    const successDialog = this.dialog.open(NotificationMessageComponent, {
      data: {
        heading: "Records uploaded!",
        body: "Your records were updated successfully.",
        type: "success"
      }
    });

    successDialog
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        window.location.reload();
      });
  }

  fetchDogInfo(): void {
    this.dogService
      .getDogsSummary()
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error("Failed to fetch dog info:", error);
          this.uploadError = "Failed to load dog information. Please refresh the page.";
          return of({ api_dogs: [] });
        })
      )
      .subscribe((res: any) => {
        this.dogData = res.api_dogs || [];
      });
  }
}
